// The Finance page's wording and arithmetic, kept out of the JSX so a test can
// run it directly (test/finance-page.test.js renders the views as well).
//
// Every amount the finance API sends is integer CENTS. Nothing here rounds a
// figure before it is displayed, and a missing figure is a dash — never $0.00,
// which would be a claim that it was measured and came to nothing.

export const MINUS = '−';

// Cents → "$1,234.56". Negative with a leading minus ("−$12.00"). `compact`
// is for KPI tiles and chart axes: "$94.7k", "$1,234", "$54.84". `sign` puts
// a "+" on a positive figure, for deltas and movements.
export function money(cents, { compact = false, sign = false } = {}) {
  if (cents == null || cents === '' || !Number.isFinite(Number(cents))) return '—';
  const n = Number(cents) / 100;
  const a = Math.abs(n);
  let body;
  if (compact && a >= 1e6) body = `${(a / 1e6).toFixed(2)}M`;
  else if (compact && a >= 1e4) body = `${(a / 1e3).toFixed(1)}k`;
  else if (compact && a >= 100) body = Math.round(a).toLocaleString('en-US');
  else body = a.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const pre = n < 0 ? MINUS : (sign && n > 0 ? '+' : '');
  return `${pre}$${body}`;
}

export function pct(v, { sign = false } = {}) {
  if (v == null || !Number.isFinite(Number(v))) return '—';
  const n = Number(v);
  const pre = n < 0 ? MINUS : (sign && n > 0 ? '+' : '');
  return `${pre}${Math.abs(n).toFixed(1)}%`;
}

// Runway in months. The backend sends null when the company is not burning
// (net cash in), which is a fact, not a gap — so it gets words, not a dash.
export function runwayText(months) {
  if (months === null) return 'not burning';
  if (months == null || !Number.isFinite(Number(months))) return '—';
  const m = Number(months);
  return `${m >= 100 ? Math.round(m) : m.toFixed(1)} mo`;
}

// ─── Dates ───────────────────────────────────────────────────────────────────
// Keys are company (Arizona) calendar days and months. They are split by hand:
// `new Date('2026-08-03')` is UTC midnight, which is the evening before for
// anyone west of Greenwich.
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTH_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export function monthLabel(key, { long = false, year = true } = {}) {
  const m = /^(\d{4})-(\d{2})/.exec(key || '');
  if (!m) return key || '—';
  const name = (long ? MONTH_LONG : MON)[Number(m[2]) - 1];
  if (!name) return key;
  return year ? `${name} ${m[1]}` : name;
}

export function dayLabel(key) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key || '');
  if (!m) return key || '—';
  return `${MON[Number(m[2]) - 1]} ${Number(m[3])}`;
}

// An ISO timestamp → "Oct 28". Null is a dash: `new Date(null)` is 1970.
export function shortDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function stampLabel(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

// ─── Product families ────────────────────────────────────────────────────────
// Fixed order, so a family keeps its colour whichever families have revenue
// this week. The colours are CSS variables (App.css, --fam-*), stepped for
// each theme.
export const FAMILIES = ['hosting', 'vps', 'hosting-addon', 'email', 'ai-visibility', 'domain', 'professional-services', 'other'];
export const FAMILY_LABEL = {
  hosting: 'Hosting', vps: 'VPS', 'hosting-addon': 'Hosting add-ons', email: 'Email',
  'ai-visibility': 'AI Visibility', domain: 'Domains', 'professional-services': 'Professional services',
  other: 'Other', unattributed: 'Unattributed',
};
export const familyLabel = (f) => FAMILY_LABEL[f] || f;
export const familyColor = (f) => {
  const i = FAMILIES.indexOf(f);
  return `var(--fam-${i === -1 ? FAMILIES.length : i + 1})`;
};

// A family whose realised margin for the month is under the floor (10%,
// lib/cost-floor.js in squish-backend). A family with no revenue has no
// margin percentage and is not flagged.
export function underFloor(family, floorPct) {
  const p = family?.marginPct;
  if (p == null || !Number.isFinite(Number(p))) return false;
  const floor = Number.isFinite(Number(floorPct)) ? Number(floorPct) : 10;
  return Number(p) < floor;
}

// ─── Categories ──────────────────────────────────────────────────────────────
const CATEGORY_LABEL = {
  'supplier-cogs': 'Supplier (COGS)', infrastructure: 'Infrastructure', software: 'Software',
  'payroll-contractors': 'Payroll / contractors', tax: 'Tax', fees: 'Fees', transfer: 'Transfer',
  owner: 'Owner', other: 'Other', uncategorised: 'Uncategorised',
};
export const categoryLabel = (c) => (c ? CATEGORY_LABEL[c] || c : '—');

// ─── Findings ────────────────────────────────────────────────────────────────
// What each irregularity rule is called on screen. The slug is the backend's
// id for the rule, not something to show Casey.
export const RULE_LABELS = {
  'unexpected-money-in': 'Unexpected money in',
  'duplicate-charge': 'Possible duplicate charge',
  'new-vendor': 'First payment to a new vendor',
  'large-outflow': 'Large outflow',
  'payout-unmatched': 'Stripe payout never reached the bank',
  'dispute-open': 'Open dispute',
  'recurring-missing': 'Regular vendor charge missing',
  uncategorised: 'Uncategorised transactions',
  'vendor-spend-rise': 'Vendor spend up',
  'refund-rate': 'Refund rate up',
  'failed-charge-rate': 'Failed-charge rate up',
  'mrr-churn': 'MRR churn up',
  'margin-under-floor': 'Margin under the 10% floor',
  'crm-tie-out': 'CRM and Stripe revenue disagree',
  runway: 'Runway under 6 months',
  'plan-gap': 'Behind plan',
};

// A rule this page has not heard of yet still reads as words.
export function ruleLabel(rule) {
  if (!rule) return 'Finding';
  if (RULE_LABELS[rule]) return RULE_LABELS[rule];
  const words = String(rule).replace(/[-_]+/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

const SEVERITY_RANK = { high: 0, medium: 1, low: 2 };

// High first, then the longest-standing first within a severity.
export function sortFindings(list) {
  return [...(list || [])].sort((a, b) => {
    const s = (SEVERITY_RANK[a?.severity] ?? 3) - (SEVERITY_RANK[b?.severity] ?? 3);
    if (s) return s;
    return String(a?.firstSeenAt || '').localeCompare(String(b?.firstSeenAt || ''));
  });
}

// ─── Vendor rules ────────────────────────────────────────────────────────────
// The backend's key rule is ^[a-z0-9][a-z0-9-]{1,62}$, and a name pattern needs
// 3+ letters or digits. A counterparty that cannot make a valid rule (a name
// of punctuation) can still be classified one row at a time.
export function vendorKeyFor(name) {
  return String(name || '')
    .toLowerCase()
    .normalize('NFKD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 63)
    .replace(/-+$/, '');
}

export function canMakeRule(name) {
  const alnum = String(name || '').replace(/[^A-Za-z0-9]/g, '').length;
  return alnum >= 3 && /^[a-z0-9][a-z0-9-]{1,62}$/.test(vendorKeyFor(name));
}

// "Always": one rule per counterparty, matching its name, for the direction
// the money moved (a vendor refund coming IN is not the vendor's bill).
export function ruleForCounterparty(row, category) {
  return {
    key: vendorKeyFor(row?.counterpartyName),
    name: row?.counterpartyName,
    category,
    namePatterns: [row?.counterpartyName],
    direction: row?.direction === 'in' || row?.direction === 'out' ? row.direction : null,
  };
}

// ─── Trends ──────────────────────────────────────────────────────────────────
// How much `field` moved INTO the period `key` from the period before it in
// the trend (the last period when key is null). Null when either side is
// missing: a delta against a gap would be invented.
export function changeAt(trend, key, field) {
  const rows = trend || [];
  const i = key == null ? rows.length - 1 : rows.findIndex((r) => r?.key === key);
  if (i < 1) return null;
  const now = rows[i]?.[field];
  const before = rows[i - 1]?.[field];
  if (now == null || before == null || !Number.isFinite(Number(now)) || !Number.isFinite(Number(before))) return null;
  return Number(now) - Number(before);
}

// The movement lines in the order they are read, and which way each moves MRR.
// The backend reports contraction and churn as amounts lost; they are drawn
// as negatives whichever sign they arrive with.
export const MOVEMENTS = [
  { key: 'new', label: 'New', dir: 1 },
  { key: 'expansion', label: 'Expansion', dir: 1 },
  { key: 'reactivation', label: 'Reactivation', dir: 1 },
  { key: 'contraction', label: 'Contraction', dir: -1 },
  { key: 'churn', label: 'Churn', dir: -1 },
];
export function movementCents(movements, m) {
  const v = movements?.[`${m.key}Cents`];
  if (v == null || !Number.isFinite(Number(v))) return null;
  return m.dir * Math.abs(Number(v));
}

// Any finance call that came back 403. The page then says one sentence and
// shows nothing else: the figures are the owner's, and a half-drawn page
// would imply some of them were not.
export function isRestricted(slots) {
  return (slots || []).some((s) => s?.error?.status === 403);
}

export const RESTRICTED_MESSAGE = 'Finance is restricted to its owner.';
